import React, { useEffect, useState } from 'react';
import DateRangePicker, { DateRangePreset } from '@components/common/DateRangePicker';
import Table, { TableHeaderContainerV2 } from '@components/common/Table';
import PaginationComponent from '@components/pagination';
import { userTicketListColumn } from '@data/columns';
import { getUserTicketList } from '@lib/api';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import Format from '@util/formatter';

interface TicketTabProps {
  merchantId: number;
  phoneNum: string;
  setIsLoading: (loading: boolean) => void;
}

const TicketTab = ({ merchantId, phoneNum, setIsLoading }: TicketTabProps) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();

  const [startDate, setStartDate] = useState(dayjs().subtract(3, 'month').add(1, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [totalLength, setTotalLength] = useState(0);
  const [dataList, setDataList] = useState<any[]>([]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const dateRange = Format.formatDateRange(startDate, endDate);
      const { data } = await createRequest(
        getUserTicketList,
        merchantId,
        dateRange.start,
        dateRange.end,
        currentPage,
        postsPerPage,
        phoneNum,
      );
      setDataList(
        data.result.map((item) => ({
          ...item,
          T_isUsing:
            item.isUsing === 1 ? (
              <div>사용중</div>
            ) : item.isUsing === 2 ? (
              <div style={{ color: '#F04E36' }}>시간 초과</div>
            ) : item.lastOut ? (
              <div style={{ color: '#53852A' }}>퇴장완료</div>
            ) : (
              <div>사용전</div>
            ),
          type:
            item.type === 1
              ? ' 일회권'
              : item.type === 2
              ? ` 기간권 - ${item.seatType === 0 ? '자유석' : '고정석'}`
              : item.type === 3
              ? ' 시간정액권'
              : item.type === 4
              ? ' 세미나실'
              : '사물함',
          start: dayjs(item.startDate).format('YYYY-MM-DD HH:mm:ss'),
          end: dayjs(item.endDate).format('YYYY-MM-DD HH:mm:ss'),
        })),
      );
      setTotalLength(data.count);
    } catch (error) {
      catchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (merchantId && phoneNum) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, phoneNum, currentPage, startDate, endDate]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') setStartDate(value);
    else if (name === 'endDate') setEndDate(value);
    setCurrentPage(1);
  };

  return (
    <>
      <TableHeaderContainerV2>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          defaultPreset={DateRangePreset.QUARTER_YEAR}
          maxPeriod={DateRangePreset.YEAR}
        />
      </TableHeaderContainerV2>
      <div className="datatable-container">
        <Table tableHeight={500} data={dataList} columns={userTicketListColumn} striped={true} />
      </div>
      {totalLength > 0 && (
        <PaginationComponent
          setCurrentPage={setCurrentPage}
          totalLength={totalLength}
          postsPerPage={postsPerPage}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default TicketTab;
